/**
 * webmAudio.js — turns a recording's bytes into what AudioTrackPlayer needs: its length, a version the browser
 * can seek in, and its waveform.
 *
 * Visit recordings are WebM/Opus straight out of Chrome's MediaRecorder (the AI scribe and the recorder bot). That
 * muxer writes the file as a live stream: the Segment and every Cluster have an "unknown" size, Info carries no
 * Duration and there are no Cues. Chrome then reports the length as Infinity and can't seek in it. So:
 *   - readWebm walks the EBML once and indexes the Opus frames (time + byte range) and the Info element;
 *   - withWebmDuration writes a Duration into Info, built from Blob slices so the audio is never copied;
 *   - decodePeaks feeds the frames to WebCodecs' AudioDecoder one by one and keeps only the loudest sample per
 *     1/50 s. decodeAudioData would decode the whole file at 48 kHz first — about 690 MB per hour of audio.
 * Anything else (another container or codec, or a browser without AudioDecoder) falls back to decodeAudioData for
 * files small enough to decode in memory, and plays without a waveform above that.
 *
 * WebCodecs globals are reached through `window.` — the project's ESLint globals predate them.
 */

// Waveform resolution: one peak per 20 ms, ~720 KB of Float32 per hour of audio
export const PEAKS_PER_SECOND = 50

// Frames handed to the decoder before waiting for it to catch up
const MAX_DECODE_QUEUE = 32
// How often a partial waveform is reported while decoding
const PROGRESS_INTERVAL_MS = 250
// decodeAudioData decodes at the file's own rate before resampling; past this it would hold too much at once
const FALLBACK_MAX_BYTES = 16 * 1024 * 1024
// Rate the fallback resamples to — plenty for an amplitude envelope
const FALLBACK_SAMPLE_RATE = 8000
// How long the fallback waits for the element to read a file's length
const PROBE_TIMEOUT_MS = 20000
// MediaRecorder's TimecodeScale (1 ms) — the EBML default when Info leaves it out
const DEFAULT_TIMECODE_SCALE = 1000000
// Opus always decodes at 48 kHz, whatever the track header claims
const OPUS_SAMPLE_RATE = 48000

// EBML element ids, compared with their length-marker bits as they appear in the file
const ID = Object.freeze({
  EBML: 0x1a45dfa3,
  SEGMENT: 0x18538067,
  SEEK_HEAD: 0x114d9b74,
  INFO: 0x1549a966,
  TIMECODE_SCALE: 0x2ad7b1,
  DURATION: 0x4489,
  TRACKS: 0x1654ae6b,
  TRACK_ENTRY: 0xae,
  TRACK_NUMBER: 0xd7,
  CODEC_ID: 0x86,
  CODEC_PRIVATE: 0x63a2,
  AUDIO: 0xe1,
  SAMPLING_FREQUENCY: 0xb5,
  CHANNELS: 0x9f,
  CLUSTER: 0x1f43b675,
  TIMECODE: 0xe7,
  SIMPLE_BLOCK: 0xa3,
  BLOCK_GROUP: 0xa0,
  BLOCK: 0xa1,
  CUES: 0x1c53bb6b
})

// Elements the walk steps into rather than over — always, even when their size is unknown
const CONTAINERS = new Set([ID.SEGMENT, ID.INFO, ID.TRACKS, ID.TRACK_ENTRY, ID.AUDIO, ID.CLUSTER, ID.BLOCK_GROUP])

// Lacing bits of a (Simple)Block's flags. MediaRecorder never laces; a laced file goes to the fallback.
const LACING_MASK = 0x06

/**
 * EBML variable-length integer at `pos`: the count of leading zero bits in the first byte gives the length (1–8
 * bytes). An id keeps its marker bit; a size drops it, and a size whose value bits are all 1 means "unknown"
 * (MediaRecorder's Segment and Clusters). Multiplication, not shifts, so sizes past 32 bits stay exact.
 * Null when the bytes end inside it or it isn't valid.
 */
function readVint(bytes, pos, keepMarker) {
  if (pos >= bytes.length) return null
  const first = bytes[pos]
  if (first === 0) return null
  let length = 1
  while (!(first & (0x80 >> (length - 1)))) length++
  if (pos + length > bytes.length) return null
  const valueBits = (0x80 >> (length - 1)) - 1
  let value = keepMarker ? first : first & valueBits
  let allOnes = (first & valueBits) === valueBits
  for (let i = 1; i < length; i++) {
    value = value * 256 + bytes[pos + i]
    if (bytes[pos + i] !== 0xff) allOnes = false
  }
  return { value, length, unknown: !keepMarker && allOnes }
}

// One element's header: its id, its body's size and where the body starts. Null when the bytes end inside it.
function readElementHeader(bytes, pos) {
  const id = readVint(bytes, pos, true)
  if (!id) return null
  const size = readVint(bytes, pos + id.length, false)
  if (!size) return null
  return {
    id: id.value,
    size: size.value,
    unknownSize: size.unknown,
    sizeLength: size.length,
    dataStart: pos + id.length + size.length
  }
}

function readUint(bytes, start, end) {
  let value = 0
  for (let i = start; i < end; i++) value = value * 256 + bytes[i]
  return value
}

function readFloat(view, start, size) {
  if (size === 4) return view.getFloat32(start)
  if (size === 8) return view.getFloat64(start)
  return 0
}

function readAscii(bytes, start, end) {
  let text = ''
  for (let i = start; i < end && bytes[i] !== 0; i++) text += String.fromCharCode(bytes[i])
  return text
}

/**
 * Index an Opus-in-WebM file: codec, channels, every frame's byte range and start time, and where Info sits (for
 * withWebmDuration). One flat pass — containers are stepped into, everything else is skipped by its size — so the
 * unknown sizes MediaRecorder writes never matter. Stops at a second Segment (an <audio> element plays only the
 * first) and keeps the frames before a cut-off last element (an upload that ended mid-block).
 *
 * @param {Uint8Array} bytes - the whole file
 * @returns {Object|null} null when it isn't EBML, has no Opus track, or uses lacing (the caller falls back)
 */
export function readWebm(bytes) {
  if (!(bytes instanceof Uint8Array)) throw Error('readWebm: bytes must be a Uint8Array')
  const first = readElementHeader(bytes, 0)
  if (!first || first.id !== ID.EBML) return null

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const tracks = []
  const blocks = { tracks: [], offsets: [], sizes: [], timecodes: [] }
  let timecodeScale = DEFAULT_TIMECODE_SCALE
  let info = null
  let segmentSizeKnown = false
  let hasSeekIndex = false
  let segments = 0
  let clusterTimecode = 0
  let pos = 0

  while (pos < bytes.length) {
    const el = readElementHeader(bytes, pos)
    if (!el) break
    const end = el.unknownSize ? bytes.length : el.dataStart + el.size

    if (CONTAINERS.has(el.id)) {
      if (el.id === ID.SEGMENT) {
        if (++segments > 1) break
        segmentSizeKnown = !el.unknownSize
      } else if (el.id === ID.INFO) {
        info = {
          start: pos,
          dataStart: el.dataStart,
          dataEnd: end,
          sizeLength: el.sizeLength,
          unknownSize: el.unknownSize,
          hasDuration: false
        }
      } else if (el.id === ID.TRACK_ENTRY) {
        tracks.push({ number: null, codecId: null, codecPrivate: null, channels: 1, sampleRate: OPUS_SAMPLE_RATE })
      }
      pos = el.dataStart
      continue
    }
    if (end > bytes.length) break

    const track = tracks[tracks.length - 1]
    switch (el.id) {
      case ID.TIMECODE_SCALE:
        timecodeScale = readUint(bytes, el.dataStart, end) || DEFAULT_TIMECODE_SCALE
        break
      case ID.DURATION:
        if (info) info.hasDuration = true
        break
      case ID.SEEK_HEAD:
      case ID.CUES:
        // A seek index points at byte offsets — inserting a Duration would shift them out from under it
        hasSeekIndex = true
        break
      case ID.TRACK_NUMBER:
        if (track) track.number = readUint(bytes, el.dataStart, end)
        break
      case ID.CODEC_ID:
        if (track) track.codecId = readAscii(bytes, el.dataStart, end)
        break
      case ID.CODEC_PRIVATE:
        if (track) track.codecPrivate = bytes.slice(el.dataStart, end)
        break
      case ID.SAMPLING_FREQUENCY:
        if (track) track.sampleRate = readFloat(view, el.dataStart, el.size) || OPUS_SAMPLE_RATE
        break
      case ID.CHANNELS:
        if (track) track.channels = readUint(bytes, el.dataStart, end) || 1
        break
      case ID.TIMECODE:
        clusterTimecode = readUint(bytes, el.dataStart, end)
        break
      case ID.SIMPLE_BLOCK:
      case ID.BLOCK: {
        // track number (vint), signed 16-bit time relative to the cluster, flags, then one frame
        const trackNumber = readVint(bytes, el.dataStart, false)
        if (!trackNumber) break
        const head = el.dataStart + trackNumber.length
        if (head + 3 > end) break
        if (bytes[head + 2] & LACING_MASK) return null
        blocks.tracks.push(trackNumber.value)
        blocks.offsets.push(head + 3)
        blocks.sizes.push(end - head - 3)
        blocks.timecodes.push(clusterTimecode + view.getInt16(head))
        break
      }
    }
    pos = end
  }

  const opus = tracks.find(t => t.codecId === 'A_OPUS')
  if (!opus) return null

  const offsets = []
  const sizes = []
  const times = []
  for (let i = 0; i < blocks.offsets.length; i++) {
    if (opus.number !== null && blocks.tracks[i] !== opus.number) continue
    offsets.push(blocks.offsets[i])
    sizes.push(blocks.sizes[i])
    times.push(blocks.timecodes[i] * timecodeScale / 1e9)
  }
  if (times.length === 0) return null

  // The last frame's start plus one more packet of the same length (MediaRecorder writes fixed 20–60 ms packets)
  const last = times[times.length - 1]
  const packet = times.length > 1 ? Math.max(0, last - times[times.length - 2]) : 0.06
  return {
    codecId: opus.codecId,
    codecPrivate: opus.codecPrivate,
    channels: opus.channels,
    sampleRate: opus.sampleRate,
    timecodeScale,
    duration: Math.max(0, last) + packet,
    frames: {
      offsets: Float64Array.from(offsets),
      sizes: Float64Array.from(sizes),
      times: Float64Array.from(times)
    },
    info,
    segmentSizeKnown,
    hasSeekIndex
  }
}

// An 8-byte EBML size (0x01 + 7 bytes) — wide enough for any Info, so its own length never has to be recomputed
function encodeSize8(value) {
  const out = new Uint8Array(8)
  let remaining = value
  for (let i = 7; i >= 1; i--) {
    out[i] = remaining % 256
    remaining = Math.floor(remaining / 256)
  }
  out[0] = 0x01
  return out
}

/**
 * The same recording with a Duration element written into its Info, so Chrome knows its length and can seek. Built
 * from slices of the original Blob — the audio itself is never copied. The Segment's size must be unknown (as
 * MediaRecorder writes it) for the insert to be safe; a file that already has a Duration, a seek index (whose
 * offsets would shift) or an Info of unknown size is returned unchanged.
 *
 * @param {Blob} blob - the original file
 * @param {Object} webm - readWebm(bytes of that file)
 */
export function withWebmDuration(blob, webm) {
  if (!(blob instanceof Blob)) throw Error('withWebmDuration: expected a Blob')
  if (!webm) throw Error('withWebmDuration: expected a readWebm() result')
  const info = webm.info
  if (!info || info.hasDuration || info.unknownSize || webm.hasSeekIndex || webm.segmentSizeKnown) return blob

  // Duration is a float in TimecodeScale units (milliseconds for MediaRecorder)
  const duration = new Uint8Array(11)
  duration.set([0x44, 0x89, 0x88])
  new DataView(duration.buffer).setFloat64(3, webm.duration * 1e9 / webm.timecodeScale)

  const infoHeader = new Uint8Array(12)
  infoHeader.set([0x15, 0x49, 0xa9, 0x66])
  infoHeader.set(encodeSize8(info.dataEnd - info.dataStart + duration.length), 4)

  return new Blob([
    blob.slice(0, info.start),
    infoHeader,
    blob.slice(info.dataStart, info.dataEnd),
    duration,
    blob.slice(info.dataEnd)
  ], { type: blob.type || 'audio/webm' })
}

function abortError() {
  return new DOMException('The operation was aborted', 'AbortError')
}

function throwIfAborted(signal) {
  if (signal && signal.aborted) throw abortError()
}

/**
 * Decode an indexed Opus file frame by frame and keep the loudest sample of every 1/50 s, so memory stays flat
 * however long the visit. Null when this browser can't (no AudioDecoder, or no Opus support) or the decoder fails.
 * Rejects with an AbortError when `signal` aborts.
 */
async function decodeOpusPeaks(bytes, webm, { signal, onProgress } = {}) {
  const AudioDecoder = window.AudioDecoder
  const EncodedAudioChunk = window.EncodedAudioChunk
  // Without the dequeue event the backpressure wait below would have nothing to wake it
  if (typeof AudioDecoder !== 'function' || typeof EncodedAudioChunk !== 'function'
      || !('ondequeue' in AudioDecoder.prototype)) {
    return null
  }

  const config = { codec: 'opus', sampleRate: OPUS_SAMPLE_RATE, numberOfChannels: webm.channels }
  if (webm.codecPrivate) config.description = webm.codecPrivate
  let support = null
  try {
    support = await AudioDecoder.isConfigSupported(config)
  } catch {
    return null
  }
  if (!support || !support.supported) return null
  throwIfAborted(signal)

  const peaks = new Float32Array(Math.max(1, Math.ceil(webm.duration * PEAKS_PER_SECOND)))
  let filled = 0
  let failure = null
  let wake = null
  let scratch = new Float32Array(0)

  const decoder = new AudioDecoder({
    output: (data) => {
      try {
        const samplesPerPeak = data.sampleRate / PEAKS_PER_SECOND
        const firstSample = Math.round(data.timestamp / 1e6 * data.sampleRate)
        if (scratch.length < data.numberOfFrames) scratch = new Float32Array(data.numberOfFrames)
        for (let channel = 0; channel < data.numberOfChannels; channel++) {
          data.copyTo(scratch, { planeIndex: channel, format: 'f32-planar' })
          for (let i = 0; i < data.numberOfFrames; i++) {
            const bucket = Math.floor((firstSample + i) / samplesPerPeak)
            if (bucket < 0 || bucket >= peaks.length) continue
            const level = Math.abs(scratch[i])
            if (level > peaks[bucket]) peaks[bucket] = level
          }
        }
        const lastBucket = Math.floor((firstSample + data.numberOfFrames - 1) / samplesPerPeak)
        filled = Math.max(filled, Math.min(peaks.length, lastBucket + 1))
      } catch (err) {
        failure = failure || err
      } finally {
        // Each AudioData holds decoded PCM until closed — let it go at once
        data.close()
      }
    },
    error: (err) => {
      failure = failure || err
      if (wake) wake()
    }
  })
  const onDequeue = () => { if (wake) wake() }
  const onAbort = () => { if (wake) wake() }
  decoder.addEventListener('dequeue', onDequeue)
  if (signal) signal.addEventListener('abort', onAbort, { once: true })

  try {
    decoder.configure(config)
    const { offsets, sizes, times } = webm.frames
    let reportedAt = performance.now()
    for (let i = 0; i < offsets.length; i++) {
      if (failure) return null
      decoder.decode(new EncodedAudioChunk({
        type: 'key',
        timestamp: Math.round(Math.max(0, times[i]) * 1e6),
        data: bytes.subarray(offsets[i], offsets[i] + sizes[i])
      }))
      while (decoder.decodeQueueSize > MAX_DECODE_QUEUE && !failure && !(signal && signal.aborted)) {
        await new Promise(resolve => { wake = resolve })
        wake = null
      }
      if (failure) return null
      throwIfAborted(signal)
      if (onProgress && performance.now() - reportedAt >= PROGRESS_INTERVAL_MS) {
        reportedAt = performance.now()
        onProgress(peaks.subarray(0, filled))
      }
    }
    try {
      await decoder.flush()
    } catch (err) {
      throwIfAborted(signal)
      return null
    }
    if (failure) return null
    return peaks
  } finally {
    decoder.removeEventListener('dequeue', onDequeue)
    if (signal) signal.removeEventListener('abort', onAbort)
    if (decoder.state !== 'closed') decoder.close()
  }
}

// Loudest sample of every 1/50 s of a decoded buffer, across its channels
function peaksFromAudioBuffer(buffer) {
  const peaks = new Float32Array(Math.max(1, Math.ceil(buffer.duration * PEAKS_PER_SECOND)))
  const samplesPerPeak = buffer.sampleRate / PEAKS_PER_SECOND
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel)
    for (let i = 0; i < data.length; i++) {
      const bucket = Math.floor(i / samplesPerPeak)
      if (bucket >= peaks.length) break
      const level = Math.abs(data[i])
      if (level > peaks[bucket]) peaks[bucket] = level
    }
  }
  return peaks
}

/**
 * The length an <audio> element reads from a file it can play, for a file too big to decode here. Rejects when the
 * element can't play it, reports no finite length, or takes longer than PROBE_TIMEOUT_MS.
 */
function probeMediaDuration(blob, signal) {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio')
    const url = URL.createObjectURL(blob)
    let done = false
    const finish = (err, duration) => {
      if (done) return
      done = true
      clearTimeout(timer)
      if (signal) signal.removeEventListener('abort', onAbort)
      audio.removeAttribute('src')
      URL.revokeObjectURL(url)
      if (err) reject(err)
      else resolve(duration)
    }
    const onAbort = () => finish(abortError())
    const timer = setTimeout(() => finish(Error('The recording\'s length could not be read')), PROBE_TIMEOUT_MS)
    audio.preload = 'metadata'
    audio.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) finish(null, audio.duration)
      else finish(Error('The recording\'s length could not be read'))
    })
    audio.addEventListener('error', () => finish(Error('This browser can\'t play this recording')))
    if (signal) signal.addEventListener('abort', onAbort, { once: true })
    audio.src = url
  })
}

// Not an Opus WebM we can index: small files are decoded whole (length and waveform at once); anything bigger plays
// without a waveform, its length read by the element itself
async function prepareFallback(blob, bytes, signal) {
  if (blob.size <= FALLBACK_MAX_BYTES && typeof window.OfflineAudioContext === 'function') {
    try {
      const context = new window.OfflineAudioContext(1, 1, FALLBACK_SAMPLE_RATE)
      const buffer = await context.decodeAudioData(bytes.buffer)
      throwIfAborted(signal)
      const peaks = peaksFromAudioBuffer(buffer)
      return { duration: buffer.duration, playable: blob, decodePeaks: async () => peaks }
    } catch (err) {
      if (err && err.name === 'AbortError') throw err
      // Not decodable here — the element may still play it
    }
  }
  const duration = await probeMediaDuration(blob, signal)
  return { duration, playable: blob, decodePeaks: async () => null }
}

/**
 * Everything AudioTrackPlayer needs from one recording file.
 *
 * @param {Blob} blob - the file as downloaded
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<{ duration: number, playable: Blob, decodePeaks: Function }>}
 *   duration — seconds; playable — the Blob to hand an <audio> element (seekable);
 *   decodePeaks({ onProgress }) — resolves with a Float32Array of PEAKS_PER_SECOND peaks (0..1), or null when this
 *   browser can't draw one; onProgress gets partial views while it decodes. Callable once.
 */
export async function prepareAudio(blob, { signal } = {}) {
  if (!(blob instanceof Blob)) throw Error('prepareAudio: expected a Blob')
  throwIfAborted(signal)
  const bytes = new Uint8Array(await blob.arrayBuffer())
  throwIfAborted(signal)

  const webm = readWebm(bytes)
  if (!webm) return prepareFallback(blob, bytes, signal)

  // The encoded bytes are kept only until the waveform is drawn
  let held = bytes
  return {
    duration: webm.duration,
    playable: withWebmDuration(blob, webm),
    decodePeaks: async ({ onProgress } = {}) => {
      if (!held) throw Error('decodePeaks: already called')
      const data = held
      held = null
      return decodeOpusPeaks(data, webm, { signal, onProgress })
    }
  }
}
